import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './ProductionRootCause.module.css';
import SourceFileSummary from './SourceFileSummary';
import {
  artavaRechartsAxis,
  artavaRechartsGrid,
  artavaRechartsTooltip,
} from './chartPalette';
import { fetchLateFailureCostAnalysis } from './productionRootCause/productionRootCauseApi';

const defaultFilters = {
  startDate: '',
  endDate: '',
  ateSwVersion: '',
  stationName: '',
  selectedFamilies: [],
};

const LateFailureCost = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const topRows = useMemo(() => data.rows.slice(0, 12), [data.rows]);

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchLateFailureCostAnalysis(nextFilters);
      setData(response || { summary: null, rows: [] });
    } catch (requestError) {
      setError(requestError.message || 'Failed to load late-failure cost analysis.');
      setData({ summary: null, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    loadData(defaultFilters);
  };

  const summary = data.summary || {};

  return (
    <div className={styles.pageContainer}>
      <GoToDashboardButton />

      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Root problem idea 6</p>
          <h1 className={styles.title}>Late-Stage Failure Cost</h1>
          <p className={styles.description}>
            Focus on failures that happen late in the test flow, when the unit has already consumed most of the test time. This page scores fail steps by how late they appear and how much cycle time they waste.
          </p>
        </div>
      </header>

      <section className={styles.filterPanel}>
        <div className={styles.filterHeader}>
          <div>
            <h3>Filters</h3>
            <p>Use this page to reduce expensive late scrap. A late failure is often more costly than an early failure even when the fail count is the same.</p>
          </div>
        </div>
        <div className={styles.filterGrid}>
          <div className={styles.filterItem}>
            <label>Start date</label>
            <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>End date</label>
            <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>ATE SW version</label>
            <input value={filters.ateSwVersion} onChange={(event) => updateFilter('ateSwVersion', event.target.value)} placeholder="All versions" />
          </div>
          <div className={styles.filterItem}>
            <label>Station name</label>
            <input value={filters.stationName} onChange={(event) => updateFilter('stationName', event.target.value)} placeholder="All stations" />
          </div>
          <div className={styles.filterActions}>
            <button className={styles.primaryButton} onClick={() => loadData()} disabled={loading}>Run analysis</button>
            <button className={styles.secondaryButton} onClick={resetFilters}>Reset</button>
          </div>
        </div>
        <SourceFileSummary
          compact
          startDate={filters.startDate}
          endDate={filters.endDate}
          ateSwVersion={filters.ateSwVersion}
          selectedFamilies={filters.selectedFamilies}
          onFamiliesChange={(values) => updateFilter('selectedFamilies', values)}
          title="Source files used for dashboard charts"
        />
      </section>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metricGrid}>
        <div className={styles.metricCard}><span>Failed sessions</span><strong>{summary.failedSessions || 0}</strong></div>
        <div className={styles.metricCard}><span>Failed steps tracked</span><strong>{summary.failedStepsTracked || 0}</strong></div>
        <div className={styles.metricCard}><span>Top late-fail rate</span><strong>{summary.topLateFailureRate || 0}%</strong></div>
        <div className={styles.metricCard}><span>Top avg position</span><strong>{summary.topAvgPositionPct || 0}%</strong></div>
      </section>

      {loading ? (
        <div className={styles.loading}>Calculating late-failure cost...</div>
      ) : data.rows.length === 0 ? (
        <div className={styles.emptyState}>No late-stage failure patterns matched this slice.</div>
      ) : (
        <>
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Late failure profile</h3>
                <p>Bars show average fail position in the test flow, while the line shows how often the step fails in the final 30% of the run.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <ComposedChart data={topRows} margin={{ top: 10, right: 20, bottom: 45, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} vertical={false} />
                  <XAxis dataKey="stepNumber" angle={-35} textAnchor="end" height={75} {...artavaRechartsAxis} />
                  <YAxis yAxisId="left" {...artavaRechartsAxis} />
                  <YAxis yAxisId="right" orientation="right" {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Bar yAxisId="left" dataKey="avgPositionPct" name="Avg fail position %" fill="#4f7f99" radius={[6, 6, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="lateFailureRate" name="Late-fail %" stroke="#24745f" strokeWidth={3} dot={{ r: 4, fill: '#24745f' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.insightGrid}>
            {data.rows.slice(0, 3).map((row) => (
              <article className={styles.insightCard} key={row.stepNumber}>
                <p className={styles.eyebrow}>Late scrap driver</p>
                <h3>{row.stepNumber} - {row.stepName}</h3>
                <p>This step fails at {row.avgPositionPct}% of the test flow on average, with {row.lateFailureRate}% of its failures happening in the last third of the run.</p>
                <div className={styles.chips}>
                  <span>{row.avgDurationMinutes} min avg</span>
                  <span>{row.affectedSerials} serials</span>
                </div>
              </article>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Late-failure evidence</h3>
                <p>Prioritize steps that fail late and often, because they consume more time before the unit is rejected.</p>
              </div>
            </div>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Step</th>
                  <th>Fail occurrences</th>
                  <th>Avg position</th>
                  <th>Late-fail rate</th>
                  <th>Avg duration</th>
                  <th>Serials</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={row.stepNumber}>
                    <td>{index + 1}</td>
                    <td className={styles.stepName}>{row.stepNumber}<br /><span className={styles.muted}>{row.stepName}</span></td>
                    <td>{row.failOccurrences}</td>
                    <td>{row.avgPositionPct}%</td>
                    <td>{row.lateFailureRate}%</td>
                    <td>{row.avgDurationMinutes} min</td>
                    <td>{row.affectedSerials}</td>
                    <td>{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
};

export default LateFailureCost;
